'use client';

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBlockPart,
  useMarketplaceSource,
  useLocale,
  type MarketplaceCourseCard as SourceCourseCard,
} from '@akademi/site-builder-runtime';
import { Input, Badge, Pagination } from '@akademi/ui';
import { Search } from 'lucide-react';
import { SectionShell } from '../_shared/SectionShell';
import { type PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { asList, tx } from '../_shared/coerce';
import { textTertiary } from '../_shared/styles';
import { MarketplaceCourseCard } from '../cards/MarketplaceCourseCard';
import { type MarketPrice } from './_shared';
import { MarketplaceCard, toCourseCardProps, type MarketplaceCardData } from './_MarketplaceCard';
import { useMarketplaceCart } from './_useMarketplaceCart';

type Localized = string | Record<string, string>;

export interface MarketplaceCourseItem {
  id?: string;
  image?: string;
  title?: Localized;
  description?: Localized;
  category?: string;
  kindLabel?: string;
  kindIcon?: 'elearning' | 'program' | 'classroom' | 'video';
  attendeeCount?: number;
  likeCount?: number;
  dislikeCount?: number;
  startDate?: string;
  endDate?: string;
  provider?: string;
  providerLogo?: string;
  /** Display string ("Beginner", "İleri Seviye", …) — only rendered by the
   *  `marketplace` card style, next to the like/dislike meta row. */
  level?: string;
  price?: MarketPrice;
  listPrice?: MarketPrice;
  free?: boolean;
  purchased?: boolean;
  href?: string;
}

function toCard(item: MarketplaceCourseItem): MarketplaceCardData {
  return {
    id: item.id,
    image: item.image,
    title: item.title,
    category: item.category,
    kindLabel: item.kindLabel,
    kindIcon: item.kindIcon,
    attendeeCount: item.attendeeCount,
    likeCount: item.likeCount,
    dislikeCount: item.dislikeCount,
    startDate: item.startDate,
    endDate: item.endDate,
    provider: item.provider,
    providerLogo: item.providerLogo,
    level: item.level,
    price: item.price,
    listPrice: item.listPrice,
    free: item.free,
    purchased: item.purchased,
    href: item.href,
  };
}

export interface MarketplaceCourseGridProps {
  title?: Localized;
  /** akademi-lms-v2's catalog id — when set AND a {@link useMarketplaceSource}
   *  is mounted, the grid lists that catalog's real courses. */
  catalogId?: string;
  items?: MarketplaceCourseItem[];
  showSearch?: boolean;
  showCategoryFilter?: boolean;
  columns?: '2' | '3' | '4';
  /** Which card design to render for each item — the simple akademi-sem tile
   *  (default) or the richer `marketplace` registry card variant. */
  cardStyle?: 'simple' | 'marketplace';
  /** How many courses load per page. */
  pageSize?: number;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

function fromSource(card: SourceCourseCard): MarketplaceCourseItem {
  return {
    id: card.id,
    image: card.thumbnailUrl,
    title: card.title,
    description: card.description,
    category: card.category,
    kindLabel: card.kindLabel,
    kindIcon: card.kindIcon,
    attendeeCount: card.attendeeCount,
    likeCount: card.likeCount,
    dislikeCount: card.dislikeCount,
    startDate: card.startDate,
    endDate: card.endDate,
    provider: card.provider,
    providerLogo: card.providerLogo,
    level: card.level,
    price: card.price,
    listPrice: card.listPrice,
    free: card.freeItem,
    purchased: card.purchased,
    href: card.publicPath,
  };
}

const MP_COURSE_GRID_CSS = `
@media (max-width: 900px) { .mp-course-grid { --mp-course-grid-cols: 2 !important; } }
@media (max-width: 560px) { .mp-course-grid { --mp-course-grid-cols: 1 !important; } }
`;

const DEFAULT_ITEMS: MarketplaceCourseItem[] = [
  {
    id: 'coaching-101',
    title: 'Coaching Conversations 101',
    description: 'Structure a coaching conversation that actually changes behaviour.',
    category: 'Leadership',
    kindLabel: 'E-Learning',
    kindIcon: 'elearning',
    provider: 'Gobito Akademi',
    attendeeCount: 482,
    likeCount: 34,
    dislikeCount: 2,
    price: { amount: '349.00', currency: 'TRY' },
    listPrice: { amount: '499.00', currency: 'TRY' },
  },
  {
    id: 'delegation',
    title: 'Delegation Without Losing Control',
    description: 'A practical framework for handing off work with confidence.',
    category: 'Leadership',
    kindLabel: 'Program',
    kindIcon: 'program',
    provider: 'Gobito Akademi',
    attendeeCount: 210,
    likeCount: 12,
    dislikeCount: 0,
    free: true,
  },
  {
    id: 'feedback-loop',
    title: 'Giving Feedback People Actually Use',
    description: 'Turn feedback into a two-way conversation, not a lecture.',
    category: 'Leadership',
    kindLabel: 'Classroom',
    kindIcon: 'classroom',
    provider: 'Gobito Akademi',
    attendeeCount: 356,
    likeCount: 41,
    dislikeCount: 1,
    purchased: true,
  },
];

/**
 * Course grid inside a catalog — akademi-lms-v2's
 * `/marketplace/catalog/[detail]` course grid, with the free-text search
 * and category chip bar. With a {@link useMarketplaceSource} mounted and a
 * `catalogId`, it lists that catalog's real courses (`POST /catalog/search`)
 * and add-to-cart hits the live `/marketplace/cart`; with no source it
 * filters the authored `items` client-side — the "fallbacks stay, for
 * testing" contract.
 */
export function MarketplaceCourseGrid({
  title,
  catalogId,
  items,
  showSearch = true,
  showCategoryFilter = true,
  columns = '3',
  cardStyle = 'simple',
  pageSize = 12,
  paddingOuter,
  paddingInner,
}: MarketplaceCourseGridProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const source = useMarketplaceSource();
  const cart = useMarketplaceCart();
  const { locale } = useLocale();
  const live = Boolean(source && catalogId);

  const [fetched, setFetched] = useState<MarketplaceCourseItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [fetchStatus, setFetchStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [query, setQuery] = useState('');
  // Server-side search term — only pushed to the backend on submit (Enter),
  // exactly like akademi-lms-v2's catalog-detail page.
  const [liveQuery, setLiveQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [localCartIds, setLocalCartIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  // Back to page 1 whenever the query changes underneath the current page —
  // same idea as CourseGrid's filter-change reset.
  useEffect(() => {
    setPage(1);
  }, [catalogId, liveQuery, activeCategory]);

  useEffect(() => {
    if (!source || !catalogId) return;
    let cancelled = false;
    setFetchStatus('loading');
    source
      .listCatalogCourses(catalogId, {
        q: liveQuery,
        category: activeCategory ?? undefined,
        firstResult: (page - 1) * pageSize,
        maxResults: pageSize,
      })
      .then((listing) => {
        if (cancelled) return;
        setFetched(listing.items.map(fromSource));
        setTotal(listing.total);
        setFetchStatus('idle');
      })
      .catch(() => {
        if (!cancelled) setFetchStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [source, catalogId, liveQuery, activeCategory, page, pageSize]);

  const raw = asList<MarketplaceCourseItem>(items);
  const list = live ? (fetched ?? []) : raw.length > 0 ? raw : DEFAULT_ITEMS;

  const isInCart = (id: string) => (cart ? cart.inCart(id) : localCartIds.has(id));
  const toggleCart = (id: string) => {
    if (cart) {
      if (cart.inCart(id)) void cart.removeByItemId(id).catch(() => {});
      else void cart.add(id).catch(() => {});
      return;
    }
    setLocalCartIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const categories = useMemo(
    () => Array.from(new Set(list.map((i) => i.category).filter((c): c is string => Boolean(c)))),
    [list],
  );

  // Stay fully invisible (no title, no search bar, no skeleton) until the
  // FIRST fetch resolves — showing a skeleton grid that then vanishes for a
  // catalog with zero courses reads as broken, not as an empty bundle. An
  // error still surfaces below (there's something to tell the visitor about);
  // this only covers the in-flight gap before we know which case it is.
  const stillLoadingFirstFetch = live && fetched === null && fetchStatus !== 'error';
  if (stillLoadingFirstFetch) return null;

  // A catalog that genuinely has zero courses attached (not "your search
  // matched nothing") has no grid to show at all. Gated on `liveQuery === ''`
  // so an actual empty SEARCH result (the catalog does have courses, just
  // none matching) still shows the real empty-state message below.
  const genuinelyEmpty =
    live && fetchStatus === 'idle' && fetched !== null && fetched.length === 0 && liveQuery === '';
  if (genuinelyEmpty) return null;

  const filtered = list.filter((item) => {
    if (activeCategory && item.category !== activeCategory) return false;
    if (!query.trim()) return true;
    const haystack = `${tx(item.title)} ${tx(item.description)}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  // Live mode already paginates server-side (`firstResult`/`maxResults`
  // above) — `filtered` is one page's worth, further narrowed by the
  // as-you-type search. The fallback/authored-items path has no server to
  // page against, so it slices the fully-filtered list itself.
  const pageItems = live ? filtered : filtered.slice((page - 1) * pageSize, page * pageSize);
  const totalCount = live ? total : filtered.length;

  return (
    <SectionShell
      title={tx(title)}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      maxWidth="theme"
    >
      <style dangerouslySetInnerHTML={{ __html: MP_COURSE_GRID_CSS }} />
      {(showSearch || (showCategoryFilter && categories.length > 0)) && (
        <div
          {...part('filter')}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 'var(--space-3, 12px)',
            marginBottom: 'var(--space-5, 20px)',
          }}
        >
          {showSearch && (
            <div style={{ position: 'relative', flex: '0 1 320px', minWidth: 200 }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--color-text-tertiary, #71717a)',
                }}
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setLiveQuery(query.trim());
                }}
                onBlur={() => setLiveQuery(query.trim())}
                placeholder={t('marketplace.courseGrid.searchPlaceholder')}
                style={{ paddingLeft: 32 }}
                aria-label={t('marketplace.courseGrid.searchPlaceholder')}
              />
            </div>
          )}
          {showCategoryFilter && categories.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--space-2, 8px)', flexWrap: 'wrap' }}>
              {categories.map((cat) => (
                <Badge key={cat} asChild variant={activeCategory === cat ? 'default' : 'outline'}>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setActiveCategory((prev) => (prev === cat ? null : cat))}
                  >
                    {cat}
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          ...textTertiary,
          fontSize: 'var(--font-size-sm, 13px)',
          marginBottom: 'var(--space-3, 12px)',
        }}
      >
        {t('marketplace.courseGrid.resultCount', { count: totalCount })}
      </div>

      {live && fetchStatus === 'error' ? (
        <EmptyState
          title={t('marketplace.courseGrid.loadError.title', {
            defaultValue: "Couldn't load these courses.",
          })}
          hint={t('marketplace.courseGrid.loadError.hint', { defaultValue: 'Please try again.' })}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={t('marketplace.courseGrid.empty.title')}
          hint={t('marketplace.courseGrid.empty.hint')}
        />
      ) : (
        <>
          <div
            {...part('list')}
            className="mp-course-grid"
            style={
              {
                display: 'grid',
                '--mp-course-grid-cols': columns,
                gridTemplateColumns: `repeat(var(--mp-course-grid-cols), minmax(0, 1fr))`,
                gap: 'var(--space-5, 20px)',
              } as CSSProperties
            }
          >
            {pageItems.map((item, i) => {
              const key = item.id ?? String(i);
              return (
                <div key={key} {...part('item', i)}>
                  {cardStyle === 'marketplace' ? (
                    <MarketplaceCourseCard course={toCourseCardProps(toCard(item), locale)} />
                  ) : (
                    <MarketplaceCard
                      item={toCard(item)}
                      inCart={isInCart(key)}
                      onToggleCart={() => toggleCart(key)}
                      starSide={i % 2 === 0 ? 'left' : 'right'}
                    />
                  )}
                </div>
              );
            })}
          </div>
          {totalCount > pageSize && (
            <div style={{ marginTop: 'var(--space-5, 20px)' }}>
              <Pagination
                totalItems={totalCount}
                currentPage={page}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </SectionShell>
  );
}
